import { useCallback, useEffect, useState } from "react";
import { api, hr } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, HomeIcon, Loader2 } from "lucide-react";
import { isHoliday, isCollectiveDay } from "../lib/holidays";

const MONTH_NAMES = [
  "Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj",
  "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac",
];

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function dayCellClass(date, isWeekend, holiday, collective) {
  const base = "border-l text-center text-[10px] py-1 px-0.5";
  if (holiday) return `${base} bg-red-50 text-red-700`;
  if (collective) return `${base} bg-blue-50 text-blue-700`;
  if (isWeekend) return `${base} bg-secondary/40 text-muted-foreground`;
  return `${base}`;
}

export default function TeamCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/team/calendar?year=${year}&month=${month}`);
      setData(r.data);
    } finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const goPrev = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); }
    else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else setMonth(month + 1);
  };

  const dim = daysInMonth(year, month);
  const days = Array.from({ length: dim }, (_, i) => i + 1);

  // Build entry map by email -> day -> "GO"|"WFH"
  // Iter106: preskoči vikend + praznike da grafički prikaz odgovara stvarno
  // brojanim danima (count_workdays na backendu izuzima vikende i blagdane).
  const entriesByEmployee = {};
  if (data) {
    for (const e of [...data.go, ...data.wfh]) {
      const sd = new Date(e.start);
      const ed = new Date(e.end);
      const start = sd.getMonth() + 1 === month ? sd.getDate() : 1;
      const end = ed.getMonth() + 1 === month ? ed.getDate() : dim;
      const map = entriesByEmployee[e.employee_email] ||= {};
      for (let d = start; d <= end; d++) {
        const dt = new Date(year, month - 1, d);
        const wknd = dt.getDay() === 0 || dt.getDay() === 6;
        if (wknd || isHoliday(dt)) continue;  // ne označavaj neradne dane
        map[d] = e.type;
      }
    }
  }

  return (
    <div className="space-y-4" data-testid="team-calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-wider">Pregled tima</p>
          <h1 className="serif text-3xl sm:text-4xl font-semibold">Kalendar tima</h1>
          <p className="text-muted-foreground mt-1">
            Tko je na godišnjem (plavo) ili radi od kuće (zlatno) u odabranom mjesecu.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goPrev} data-testid="cal-prev"><ChevronLeft className="w-4 h-4"/></Button>
          <div className="serif text-lg font-medium min-w-[180px] text-center" data-testid="cal-month-label">
            {MONTH_NAMES[month - 1]} {year}.
          </div>
          <Button variant="outline" size="icon" onClick={goNext} data-testid="cal-next"><ChevronRight className="w-4 h-4"/></Button>
          <Button variant="ghost" size="sm" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); }}>Danas</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-primary inline-block rounded-sm"/> GO</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-accent inline-block rounded-sm"/> WFH</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-blue-100 border border-blue-300 inline-block rounded-sm"/> Kolektivni</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-red-50 border border-red-300 inline-block rounded-sm"/> Blagdan</span>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && (
            <div className="p-8 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Učitavanje…
            </div>
          )}
          {!loading && data && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-secondary/50 z-10 min-w-[120px] sm:min-w-[200px]">Zaposlenik</th>
                    {days.map(d => {
                      const dt = new Date(year, month - 1, d);
                      const wknd = dt.getDay() === 0 || dt.getDay() === 6;
                      const hol = isHoliday(dt);
                      const col = isCollectiveDay(dt);
                      return (
                        <th key={d} className={dayCellClass(dt, wknd, hol, col)} style={{ minWidth: "22px" }}>
                          <div>{d}</div>
                          <div className="text-[8px] opacity-60">{["N","P","U","S","Č","P","S"][dt.getDay()]}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.team.map(emp => {
                    const empEntries = entriesByEmployee[emp.email] || {};
                    return (
                      <tr key={emp.email} className="border-t hover:bg-secondary/30" data-testid={`row-${emp.email}`}>
                        <td className="p-2 sticky left-0 bg-background border-r">
                          <div className="font-medium truncate max-w-[120px] sm:max-w-[200px]">{emp.full_name}</div>
                          <div className="text-[10px] text-muted-foreground truncate max-w-[120px] sm:max-w-[200px]">{emp.katedra}</div>
                        </td>
                        {days.map(d => {
                          const type = empEntries[d];
                          const dt = new Date(year, month - 1, d);
                          const wknd = dt.getDay() === 0 || dt.getDay() === 6;
                          const hol = isHoliday(dt);
                          const col = isCollectiveDay(dt);
                          let bg = "";
                          if (type === "GO") bg = "bg-primary";
                          else if (type === "WFH") bg = "bg-accent";
                          else if (hol) bg = "bg-red-50";
                          else if (col) bg = "bg-blue-50";
                          else if (wknd) bg = "bg-secondary/30";
                          return (
                            <td key={d} className={`border-l ${bg} text-center`}
                                title={type ? `${emp.full_name}: ${type} ${d}.${month}.${year}.` : ""}
                                data-testid={type ? `cell-${emp.email}-${d}-${type}` : undefined}>
                              {type && <span className="text-[10px] text-white font-bold">{type === "GO" ? "G" : "W"}</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && (
        <div className="text-xs text-muted-foreground">
          {data.team.length} zaposlenika · {data.go.length} GO unosa · {data.wfh.length} WFH unosa za {MONTH_NAMES[month - 1]} {year}.
        </div>
      )}
    </div>
  );
}
